/**
 * apiErrorMessage — l'enveloppe d'erreur de l'api réduite à une phrase.
 *
 * `apiRequest` range `{ ...error.response.data, status }` dans la tranche ; un hook doit donc
 * normaliser avant de faire descendre quoi que ce soit au design system, qui reçoit de la copie,
 * jamais une enveloppe. Pur : aucune dépendance, testable seul.
 *
 * Quatre formes, dans cet ordre : la chaîne nue, l'erreur axios encore emballée
 * (`response.data.message` — ce que voit un appelant qui reçoit l'erreur avant la tranche), le
 * `message` aplati par `apiRequest`, puis le repli de l'appelant.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
    if (typeof error === "string" && error.trim()) return error
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const wrapped = (error as any)?.response?.data?.message
    if (typeof wrapped === "string" && wrapped.trim()) return wrapped
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const message = (error as any)?.message
    if (typeof message === "string" && message.trim()) return message
    return fallback
}

/**
 * La même enveloppe, lue comme l'ancien `ErrorTextM` (`common/components/text/ErrorTextM.js`) : une
 * erreur de validation Laravel (`errors`) rend la PREMIÈRE ligne de chacun de ses champs, une par
 * ligne ; sinon `message`, puis `error`. Pour un écran porté à parité, dont les messages du serveur
 * (« Code incorrect. Il vous reste 4 essais. », le bridage des mots de passe) doivent rester les mêmes.
 */
function apiErrorLines(error: unknown, fallback: string): string {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const envelope = error as any
    if (envelope?.errors != null && typeof envelope.errors === "object") {
        const lines = Object.values(envelope.errors as Record<string, unknown>)
            .map((field) => (Array.isArray(field) ? field[0] : field))
            .filter((line): line is string => typeof line === "string" && line.trim() !== "")
        if (lines.length > 0) return lines.join("\n")
    }
    if (typeof envelope?.message !== "string" && typeof envelope?.error === "string" && envelope.error.trim()) {
        return envelope.error
    }
    return apiErrorMessage(error, fallback)
}

export const TOO_MANY_ATTEMPTS = "Trop de tentatives. Réessayez dans une minute."

/**
 * Les écrans d'avant la connexion : la lecture de parité ci-dessus, sauf un bridage (tout 429 de ces
 * écrans, que Laravel rend « Too Many Attempts. », en anglais), dit en français (Wajih, 19/10).
 */
export function authErrorText(error: unknown, fallback: string): string {
    if ((error as { status?: unknown } | null)?.status === 429) return TOO_MANY_ATTEMPTS
    return apiErrorLines(error, fallback)
}
